import { useCallback, useEffect, useMemo, useState } from "react";
import {
  IoCheckmarkDoneOutline,
  IoDownloadOutline,
  IoEyeOutline,
  IoTrashOutline,
} from "react-icons/io5";
import {
  deleteApplication,
  listApplications,
  markApplicationRead,
} from "../../../api/adminApplicationsApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import ConfirmDialog from "../../../components/dashboard/ConfirmDialog";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { useNotify } from "../../../components/ui/NotificationProvider";
import { getApiBaseUrl } from "../../../config/apiBase";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function resolveResumeUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = getApiBaseUrl().replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

function ApplicationDetailModal({ application, busy, onClose, onMarkRead, onDelete }) {
  if (!application) return null;

  const resumeUrl = resolveResumeUrl(application.resumeUrl);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
      />
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              {application.firstName} {application.lastName}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Applied for {application.positionTitle || application.positionSlug}
            </p>
          </div>
          {!application.isRead ? (
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              New
            </span>
          ) : null}
        </div>

        <dl className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="font-medium text-slate-500">Email</dt>
            <dd className="mt-1 text-slate-900">{application.email}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Phone</dt>
            <dd className="mt-1 text-slate-900">{application.phone || "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Position slug</dt>
            <dd className="mt-1 text-slate-900">{application.positionSlug}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Submitted</dt>
            <dd className="mt-1 text-slate-900">{formatDate(application.createdAt)}</dd>
          </div>
        </dl>

        {application.message ? (
          <div className="mt-6">
            <p className="text-sm font-medium text-slate-500">Message</p>
            <p className="mt-2 whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm text-slate-800">
              {application.message}
            </p>
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap gap-2">
          {resumeUrl ? (
            <a
              href={resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-gray-50"
            >
              <IoDownloadOutline className="h-4 w-4" />
              {application.resumeFileName || "Download resume"}
            </a>
          ) : null}

          {!application.isRead ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onMarkRead(application, true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              <IoCheckmarkDoneOutline className="h-4 w-4" />
              Mark as read
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => onMarkRead(application, false)}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
            >
              Mark as unread
            </button>
          )}

          <button
            type="button"
            disabled={busy}
            onClick={() => onDelete(application)}
            className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            <IoTrashOutline className="h-4 w-4" />
            Delete
          </button>

          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-slate-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ApplicationsManager() {
  const notify = useNotify();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadApplications = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listApplications();
      setApplications(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return applications.filter((app) => {
      if (filter === "unread" && app.isRead) return false;
      if (filter === "read" && !app.isRead) return false;
      if (!q) return true;

      const haystack = [
        app.firstName,
        app.lastName,
        app.email,
        app.positionTitle,
        app.positionSlug,
        app.phone,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [applications, query, filter]);

  async function handleMarkRead(application, isRead) {
    setBusy(true);
    try {
      const updated = await markApplicationRead(application.id, isRead);
      setApplications((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item))
      );
      setSelected((prev) => (prev?.id === updated.id ? updated : prev));
      notify.success(isRead ? "Marked as read" : "Marked as unread");
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function openApplication(application) {
    setSelected(application);
    if (!application.isRead) {
      try {
        const updated = await markApplicationRead(application.id, true);
        setApplications((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item))
        );
        setSelected(updated);
      } catch {
        // Keep modal open even if mark-read fails.
      }
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteApplication(deleteTarget.id);
      setApplications((prev) =>
        prev.filter((item) => item.id !== deleteTarget.id)
      );
      if (selected?.id === deleteTarget.id) setSelected(null);
      notify.success("Application deleted");
      setDeleteTarget(null);
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingState message="Loading applications…" />;
  if (error) return <ErrorState message={error} onRetry={loadApplications} />;

  const unreadCount = applications.filter((a) => !a.isRead).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">
            Job applications
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {applications.length} total
            {unreadCount > 0 ? ` · ${unreadCount} unread` : ""}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, or position…"
          className="w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
        />

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-400"
        >
          <option value="all">All</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title={query || filter !== "all" ? "No matching applications" : "No applications yet"}
          description={
            query || filter !== "all"
              ? "Try a different search or filter."
              : "Applications submitted from the careers page will appear here."
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">
                  Applicant
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 md:table-cell">
                  Position
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 lg:table-cell">
                  Submitted
                </th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((app) => (
                <tr
                  key={app.id}
                  className={`hover:bg-slate-50/80 ${!app.isRead ? "bg-emerald-50/30" : ""}`}
                >
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-900">
                        {app.firstName} {app.lastName}
                        {!app.isRead ? (
                          <span className="ml-2 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                            New
                          </span>
                        ) : null}
                      </p>
                      <p className="truncate text-xs text-slate-500">{app.email}</p>
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 text-slate-600 md:table-cell">
                    {app.positionTitle || app.positionSlug}
                  </td>
                  <td className="hidden px-4 py-3 text-slate-600 lg:table-cell">
                    {formatDate(app.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openApplication(app)}
                        className="rounded-lg border border-gray-200 p-2 text-slate-600 hover:bg-gray-50"
                        aria-label={`View ${app.firstName} ${app.lastName}`}
                      >
                        <IoEyeOutline className="h-4 w-4" />
                      </button>
                      {app.resumeUrl ? (
                        <a
                          href={resolveResumeUrl(app.resumeUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-lg border border-gray-200 p-2 text-slate-600 hover:bg-gray-50"
                          aria-label={`Download resume for ${app.firstName}`}
                        >
                          <IoDownloadOutline className="h-4 w-4" />
                        </a>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(app)}
                        className="rounded-lg border border-gray-200 p-2 text-red-600 hover:bg-red-50"
                        aria-label={`Delete ${app.firstName} ${app.lastName}`}
                      >
                        <IoTrashOutline className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ApplicationDetailModal
        application={selected}
        busy={busy}
        onClose={() => setSelected(null)}
        onMarkRead={handleMarkRead}
        onDelete={(app) => {
          setSelected(null);
          setDeleteTarget(app);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete application?"
        message={
          deleteTarget
            ? `This will permanently delete the application from ${deleteTarget.firstName} ${deleteTarget.lastName}.`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
